import { useState } from "react";
import { ChevronDown, Phone, Mail, Globe, Facebook, Instagram, Youtube, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const INFO_SECTIONS = [
  {
    title: "About Us - Unique Deal India",
    body: "Unique Deal India is an education-first opportunity platform that helps learners earn while they learn. We combine skill-based courses with a transparent referral rewards structure.",
  },
  {
    title: "Our Mission",
    body: "To empower every Indian with practical digital skills and a fair earning ladder — where effort, learning and community growth translate directly into rewards.",
  },
  {
    title: "Our Vision",
    body: "A future where financial independence is powered by knowledge. We envision millions of members turning learning into a lifelong income.",
  },
  {
    title: "What Makes Us Unique",
    body: "Real courses, real rewards, transparent ranks and unlimited direct referrals. No hidden fees. A single dashboard that shows every rupee earned.",
  },
  {
    title: "Terms & Conditions – Unique Deal India",
    body: "By using this platform you agree to our fair-use, anti-spam and anti-fraud policies. Referral incomes are conditional on active accounts. Full terms available on request.",
  },
];

function Accordion() {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-2">
      {INFO_SECTIONS.map((s, i) => {
        const isOpen = open === i;
        return (
          <div
            key={s.title}
            data-testid={`info-accordion-item-${i}`}
            className="rounded-lg bg-black/30 border border-white/10 overflow-hidden"
          >
            <button
              data-testid={`info-accordion-btn-${i}`}
              onClick={() => setOpen(isOpen ? -1 : i)}
              className="w-full flex items-center justify-between px-4 py-3 text-left text-white hover:bg-white/5 transition-colors"
            >
              <span className="text-sm font-medium">{s.title}</span>
              <ChevronDown
                size={16}
                className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
            <div
              className={`grid transition-all duration-300 ease-out ${
                isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <p className="px-4 pb-3 text-xs leading-relaxed text-white/75">{s.body}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function XIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" {...props}>
      <path d="M18.244 2H21.5l-7.19 8.213L22.75 22h-6.89l-5.38-6.994L4.25 22H1l7.68-8.774L1.25 2h6.89l4.85 6.4L18.244 2Zm-1.207 18h1.86L6.99 4H5.02l12.017 16Z" />
    </svg>
  );
}

export default function AuthFooter() {
  return (
    <footer
      data-testid="auth-footer"
      className="w-full text-white"
      style={{
        background:
          "linear-gradient(180deg, #3f0a0a 0%, #1f0505 100%)",
      }}
    >
      <div className="max-w-5xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Office Details */}
        <div data-testid="office-details">
          <h3
            className="text-xl font-bold tracking-tight border-b border-white/15 pb-2 mb-4"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Office Details
          </h3>
          <div className="space-y-4 text-sm">
            <div>
              <div className="text-amber-400 font-bold">Reg. Office:</div>
              <div className="mt-1 leading-relaxed text-white/85">
                Ishika Smart Offer Private Limited
                <br />
                Gargariya, Kankomath Dhanbad, Jharkhand
                <br />
                Pin code – 828113
              </div>
            </div>
            <div>
              <div className="text-amber-400 font-bold">Corporate Office:</div>
              <div className="mt-1 leading-relaxed text-white/85">
                Angarpathra, Katras, Dhanbad, Jharkhand-828113
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Phone size={16} className="text-amber-400" />
              <span className="text-amber-400 font-semibold">Customer Helpline:</span>
              <a href="tel:+917352508712" className="text-white/90 hover:text-white">
                +91 7352508712
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Mail size={16} className="text-amber-400" />
              <span className="text-amber-400 font-semibold">Email:</span>
              <a
                href="mailto:Support@uniquedealindia.com"
                className="text-white/90 hover:text-white break-all"
              >
                Support@uniquedealindia.com
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-amber-400" />
              <span className="text-amber-400 font-semibold">Web App:</span>
              <span className="text-white/90">uniquedeal.vercel.app</span>
            </div>
          </div>
        </div>

        {/* Information Details */}
        <div data-testid="information-details">
          <h3
            className="text-xl font-bold tracking-tight border-b border-white/15 pb-2 mb-4"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Information Details
          </h3>
          <Accordion />
        </div>
      </div>

      {/* Copyright + socials */}
      <div className="border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-6 flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-white/85">
            © 2026 uniquedealindia. All Rights Reserved.
          </p>
          <p className="text-[11px] text-white/55">
            Powered by: Ishika Smart Offer Pvt. Ltd.
          </p>
          <div className="flex items-center gap-3 mt-2">
            {[
              { Icon: Facebook, href: "#", label: "Facebook", testId: "social-facebook" },
              { Icon: Instagram, href: "#", label: "Instagram", testId: "social-instagram" },
              { Icon: Youtube, href: "#", label: "YouTube", testId: "social-youtube" },
              { Icon: XIcon, href: "#", label: "X", testId: "social-x" },
            ].map(({ Icon, href, label, testId }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                data-testid={testId}
                className="h-9 w-9 rounded-full bg-black/50 border border-white/10 flex items-center justify-center text-white/80 hover:text-white hover:border-white/30 hover:bg-black/70 transition-colors"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
